import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import FaQuestion from 'react-icons/lib/fa/question-circle-o';
import ReactTooltip from 'react-tooltip';
import { GetBrandedAppName, GetBrandedCompanyName, GetBrandedCurriculumName } from '../../services/BrandingService';

export const tooltipTypes = {
  scoreType: 'scoreType',
  scale: 'scale',
  benchmarks: 'benchmarks',
  metric: 'metric',
  curriculum: 'curriculum',
  score: 'score',
  pr: 'pr',
  readingLevel: 'readingLevel',
  area: 'area',
  phonologicalAwareness: 'phonologicalAwareness',
  sightRecognition: 'sightRecognition',
  vocabulary: 'vocabulary',
  decoding: 'decoding',
  resourcesAndExamples: 'resourcesAndExamples',
  appropriatelyChallengingSkills: 'appropriatelyChallengingSkills',
  riskLevel: 'riskLevel',
  atRisk: 'atRisk',
  screeningWindows: 'screeningWindows',
  progressKey: 'progressKey'
}

const getTooltipText = (type) => {
  const appName = GetBrandedAppName();
  switch (type) { 
    case (tooltipTypes.scoreType): 
      return 'Adjusted scores account for differences in the difficulty of the passages read.';
    case (tooltipTypes.scale):
      return 'Change the scale to Lexile, DRA, or WCPM.';
    case (tooltipTypes.benchmarks): 
      return `National benchmarks are based on the results from students across the country.  If present, District benchmarks are provided to ${appName} by your District.`;
    case (tooltipTypes.metric):  
      return 'Change the scale to Oral Reading Fluency, Amira Reading Mastery, Reading Mastery, High Frequency Words, Phonological Awareness, or Vocabulary Size.';
    case (tooltipTypes.curriculum):  
      return `Choose a curriculum to view its scope & sequence in this report. To see your students' progress through the early reader skill scaffold, choose "${GetBrandedCurriculumName()}".`;
    case (tooltipTypes.score):   
      return 'This shows student\'s most recent assessment score and the date it was taken or rescored.';
    case (tooltipTypes.pr):   
      return 'The PR is the student\'s Percentile Ranking, based on the student\'s performance compared to other students associated with the selected benchmark.';
    case (tooltipTypes.readingLevel):   
      return 'In Science of Reading, students are not leveled. This data should be used only as an indication of the text complexity the student is able to tackle without frustration.';
    case (tooltipTypes.area):   
      return 'AREA stands for Amira Reading Estimated Age. <br />The value tells you how this student compares to others as an overall reader.   Essentially, the student is placed into a cohort of students nationally. The typical age of the students in that cohort is this student\'s AREA score.';
    case (tooltipTypes.phonologicalAwareness):   
      return `This tab shows a student\'s Percentile Ranking in relationship to all students nationally. The skills reflect a scope and sequence for Letter-Sound Correspondence mastery. ${appName} is tracking about 180 specific letter-sounds`;
    case (tooltipTypes.sightRecognition):   
      return `This tab shows a student\'s Percentile Ranking in relationship to all students nationally. The skills are specific high frequency words that students should ultimately read via sight recognition. ${appName} is tracking about 400 high frequency words, most from the Dolch list.`;
    case (tooltipTypes.vocabulary):   
      return 'This tab shows a student\'s Percentile Ranking in relationship to all students nationally. The skills reflect the Into Reading scope and sequence for Vocabulary.';
    case (tooltipTypes.decoding):   
      return 'This tab shows a student\'s Percentile Ranking in relationship to all students nationally.  The score reflects a student\'s ability to segment and blend words, and read grade-level appropriate words.';
    case (tooltipTypes.resourcesAndExamples):   
      return 'We recommend these resources to address the gaps in the student\'s mastery, as identified above.  Each resource is selected to target a student\'s current abilities and growth areas.';
    case (tooltipTypes.appropriatelyChallengingSkills):   
      return 'These skills should be emphasized in individual and small group instruction.';
    case (tooltipTypes.riskLevel):   
      return `Risk is not diagnosis. ${appName} provides an early warning if a student seems to be evidencing the reading behaviors associated with Dyslexia.`;
    case (tooltipTypes.atRisk):   
      return 'Any student scoring above 30 is "at risk". Stronger signals vary by grade but typically are associated with scores in the mid-thirties and above.';
    case (tooltipTypes.screeningWindows):   
      return 'Within the date range specified, students who haven’t yet completed a benchmark assessment in the current screening window will be automatically presented with one the next time they log in.';
    case (tooltipTypes.progressKey):   
      return `The blue line on the chart shows how the student\'s mastery is changing. <br /><br />The triangle indicates the predicted mastery level by the end of the student year. <br /><br />This progress line reflects the improvement ${appName} is documenting in the student\'s reading.  The line can show significant growth, very gradual growth or even, rarely, a slide in the student\'s fluency.`
    default:
      return;
  }
}

export const Tooltip = (props) => {
  const {type, content, displayedText} = props;

  const id = useMemo(() => {
    return type + 'Tooltip';
  }, [type]); 
  const textId = useMemo(() => {
    return id + 'Text';
  }, [id]); 
  const text = useMemo(() => {
    if (displayedText || content) {
      return content;
    } else {
    return getTooltipText(type);
    }
  }, [displayedText, content, type]);

  return (
    <span className="amiraTooltipContainer" data-tip data-for={id} data-html={true}>
      <FaQuestion className="amiraTooltipIcon" size="18"/>
      <ReactTooltip id={id} className="amiraTooltip" place="top" effect="solid">
        {`<div id=${textId} class="amiraTooltipText">${text}</div>`}
      </ReactTooltip>
    </span>
  );
}

Tooltip.propTypes = {
  type: PropTypes.string,
  content: PropTypes.any,
}
